/* eslint-disable camelcase */
/* eslint-disable security/detect-object-injection */
import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import {
  BenchmarkType,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  ValueClass,
} from '@/api/rest/resources/types/fact';
import { hasLabel } from '@/pages/shared/hooks/utils';
import { valueToTonne } from '@/utils/formatting';
import { DateLike, MAX_ES_DATE, MIN_ES_DATE } from '@/utils/formatting/date';

import { ChartDataItem } from './ReferenceLines';
import { ChartData, GraphFact } from './types';

// extending recharts api to set the axis limits to: eg: min=80% of the data min value, max=120% of the data max value
export const minMaxAxisDomain =
  (minMultiplier: number, maxMultiplier: number, minLimit?: number, maxLimit?: number) =>
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  ([dataMin, dataMax]: [number, number], allowDataOverflow?: boolean): [number, number] => [
    minLimit && dataMin * minMultiplier < minLimit ? minLimit : dataMin * minMultiplier,
    maxLimit && dataMax * maxMultiplier > maxLimit ? maxLimit : dataMax * maxMultiplier,
  ];

export const oneYearInMilliseconds = 1 * 365 * 24 * 60 * 60 * 1000;

export const getYearDomainForNumOfYearsFromToday =
  (yearsBefore = 0, yearsAfter = 0, limitMaxYear = false) =>
  ([minYear, maxYear]: [number, number]): [number, number] =>
    [
      Math.min(minYear, new Date().getFullYear() - yearsBefore),
      limitMaxYear ? maxYear : Math.max(maxYear, new Date().getFullYear() + yearsAfter),
    ] satisfies [number, number];

/**  Science spec
 * For historic graphs -
 * - 1 tick per year, evenly spaced
 * - min = min(2018 OR min from the timeseries)
 * - max = max(currentYear OR max from the timeseries)
 * - mm/yyyy
 */
export const getXAxisDomainForHistoricGraph = (
  minDate: DateLike,
  maxDate: DateLike,
  baselineYear = 2018,
  limitMaxYear = false,
) => {
  const minYear = new Date(minDate).getFullYear();
  const maxYear = new Date(maxDate).getFullYear();

  return getYearDomainForNumOfYearsFromToday(0, 0, limitMaxYear)([Math.min(baselineYear, minYear), maxYear]);
};

/**  Science spec
 * For potential graphs -
 * - 1 tick per year, evenly spaced
 * - min = min(currentYear - 1 OR min from the timeseries)
 * - max = max(currentYear + 9 OR max from the timeseries)
 * - yyyy format
 */
export const getXAxisDomainForPotentialGraph = (minDate: DateLike, maxDate: DateLike) => {
  const minYear = new Date(minDate).getFullYear();
  const maxYear = new Date(maxDate).getFullYear();

  return getYearDomainForNumOfYearsFromToday(1, 9)([minYear, maxYear]);
};

/**  Design spec
 * For historic + potential graphs -
 * - 1 tick per year, evenly spaced
 * - min = min(2018 OR min from the timeseries)
 * - max = max(currentYear + 9 OR max from the timeseries)
 * - yyyy format
 */
export const getXAxisDomainForScenarioGraph = (minDate: DateLike, maxDate: DateLike, timeFrame: number) => {
  const minYear = new Date(minDate).getFullYear();
  const maxYear = new Date(maxDate).getFullYear();

  return getYearDomainForNumOfYearsFromToday(0, timeFrame)([Math.min(2018, minYear), maxYear]);
};

export const kgToTonneFormatter = (
  fact:
    | (Omit<GraphFact | PlotReportFactElement, 'value'> & { value?: number | null })
    | undefined
    | PlotReportFactElement<number | null>,
) => {
  if (!fact) {
    return null;
  }
  const { value, unit } = fact;
  return valueToTonne(value, unit);
};

const limitBenchmarkDatesToMaxDateInTimeSeries = (data: GraphFact[]): GraphFact[] => {
  const maxDateInTimeSeries = Math.max(
    ...data.filter((fact) => !fact.name.includes('benchmark')).map((fact) => new Date(fact.date).getTime()),
  );

  return data.map((fact) => {
    const isBenchmarkFact = fact.name.includes('benchmark');
    if (isBenchmarkFact) {
      const benchmarkDate = new Date(fact.date).getTime();

      if (benchmarkDate > maxDateInTimeSeries) {
        return {
          ...fact,
          date: new Date(maxDateInTimeSeries).toISOString().split('T')[0],
        };
      }
    }
    return fact;
  }) as GraphFact[];
};

export const getChartDataFromGraphFact = (
  data: GraphFact[],
  factNames: Record<string, string>,
  formatter?: (fact: Omit<GraphFact, 'value'> & { value?: number | null }) => number | null | undefined,
) => {
  const dataWithUpdatedBenchmarkDates = limitBenchmarkDatesToMaxDateInTimeSeries(data);

  return dataWithUpdatedBenchmarkDates.reduce((acc, curr) => {
    const {
      date,
      name,
      value,
      upper_confidence_limit,
      lower_confidence_limit,
      labels,
      confidence_interval_pct,
      metadata,
      unit,
    } = curr;

    const dataPoint = {
      value: formatter?.(curr) ?? value,
      confidence_interval: [
        formatter?.({ ...curr, value: lower_confidence_limit }) ?? lower_confidence_limit,
        formatter?.({ ...curr, value: upper_confidence_limit }) ?? upper_confidence_limit,
      ],
      confidence_interval_pct,
      labels: labels || [],
      metadata,
      unit,
    };

    const dataPointFactName = factNames[name] ?? '';

    /** Since we receive time series that may not overlap on all timestamps
     *  in such cases, we default to null (not 0, since 0 is a valid value) for that particular date
     *  The graphs will then connect these null values along the other non-null values to create a continuous graph
     *  If these points are not handled, the graph breaks since we need valid Y values along the shared X axis
     */
    const fact = { [dataPointFactName]: dataPoint ?? null };
    const otherFacts = Object.fromEntries(
      Object.keys(factNames)
        .filter((factName: string) => factName !== name)
        .map((factName) =>
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          factNames[factName] ? [factNames[factName], acc[date]?.[factNames[factName]!] ?? null] : [],
        ),
    );
    acc[date] = {
      ...fact,
      ...otherFacts,
    };

    return acc;
  }, {} as ChartData);
};

export const sortChartData = (data: ChartData, dateFormatter?: (date: DateLike) => DateLike) => {
  return (
    Object.entries(data)
      // @ts-ignore typescript does not allow arithmetic operations between date objects
      .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
      .map(([date, currentData]) => ({
        label: dateFormatter?.(date) ?? date,
        ...currentData,
      }))
  );
};

export const hasInterpolatedValues = (data: ChartData[], field: string) => {
  return data.some((item) => {
    return !!item?.[field]?.hasInterpolatedLabels;
  });
};

export const intrapolatedValues = (data: ChartDataItem[], field: string) => {
  return data.map((item) => {
    return {
      ...item,
      [field]: {
        ...item?.[field],
        hasInterpolatedLabels:
          item?.[field]?.labels &&
          hasLabel({ [PlotReportFactLabelNameEnum.value_class]: ValueClass.interpolated }, item?.[field]?.labels),
      },
    };
  });
};
/**
 * NOTE - this function expects a pre-sorted (on timestamp) object as argument for sortedData
 * using this function without timestamp-sorted will not guarantee accurate functionality
 */
export const sliceSortedChartDataToDateRange = (
  sortedData: ChartDataItem[],
  minDate: DateLike | null,
  maxDate: DateLike | null,
) => {
  const minYear = new Date(minDate ?? MIN_ES_DATE).getFullYear();
  const maxYear = new Date(maxDate ?? MAX_ES_DATE).getFullYear();

  const firstItemIndex = sortedData.findIndex((point) => minYear <= new Date(point.label).getFullYear());
  const lastItemIndex = sortedData.findLastIndex((point) => new Date(point.label).getFullYear() <= maxYear);

  return sortedData.slice(firstItemIndex, lastItemIndex + 1);
};

export const getDataDefaultDomain = (
  data: GraphFact[],
  minMultiplier = 1,
  maxMultiplier = 1,
  formatter:
    | undefined
    | ((fact: Omit<GraphFact, 'value'> & { value?: number | null }) => number | null | undefined) = undefined,
) => {
  const dataValues = data.map((fact) => (formatter ? formatter(fact) : fact?.value)).filter(Boolean);

  return [Math.min(...dataValues) * minMultiplier, Math.max(...dataValues) * maxMultiplier] as [number, number];
};

export const applyBenchmarksToChartData = <T, D>(
  baseData: T[],
  customBenchmark: CustomBenchmarkSuccessResponse | null | undefined,
  xTicksDomain: D[],
  keys: { low: string; moderate: string; high: string },
  extrapolateFn: (domain: D[], data: T[], key: string) => T[],
) => {
  if (customBenchmark) {
    return baseData.map((point) => ({
      ...point,
      [keys.low]: customBenchmark.low_value !== null ? { value: customBenchmark.low_value } : undefined,
      [keys.moderate]: customBenchmark.moderate_value !== null ? { value: customBenchmark.moderate_value } : undefined,
      [keys.high]: customBenchmark.high_value !== null ? { value: customBenchmark.high_value } : undefined,
    }));
  }

  let data = extrapolateFn(xTicksDomain, baseData, keys.low);
  data = extrapolateFn(xTicksDomain, data, keys.moderate);
  return extrapolateFn(xTicksDomain, data, keys.high);
};

export const getBenchmarkLabel = (hasData: boolean, defaultLabel: string, customName?: string | null) => {
  if (!hasData) return null;
  if (customName) return `${customName} (${defaultLabel})`;
  return defaultLabel;
};

export const getChartDataWithBenchmarks = ({
  chartData,
  benchmarks,
}: {
  chartData: GraphFact[];
  benchmarks: {
    [key in BenchmarkType]?: PlotReportFactElement<number | null> | undefined;
  };
}): GraphFact[] => {
  const benchmarkObjects = Object.entries(benchmarks);

  if (benchmarkObjects.length === 0) {
    return chartData;
  }

  return [
    ...chartData,
    ...benchmarkObjects.map(
      ([benchmarkType, benchmarkFact]) =>
        ({
          ...benchmarkFact,
          name: `${benchmarkFact?.name}_${benchmarkType}`,
          date: benchmarkFact?.measured_at,
          confidence_interval_pct: null,
        }) as GraphFact,
    ),
  ];
};
